/**
 * AiTutorTranscript
 *
 * The transcript side panel that is baked into the ai-tutor recording. It is the
 * recorder-side analog of the student's frontend `AiTutorSession` "full"
 * transcript panel: the same chronologically-merged thread of spoken
 * transcriptions (agent + student) and typed chat, the same message bubbles,
 * "thinking" indicator, headers and auto-follow-to-bottom behaviour.
 *
 * Because the recorder is a real (muted, hidden) room participant, it receives
 * the agent's + student's `lk.transcription` streams via useTranscriptions() and
 * the student's typed `lk.chat` via useChat() — no extra wiring needed. There is
 * no human viewing this page, so the panel is always-on (no toggle) and the
 * manual controls (mic / chat input / mode switch) are intentionally dropped.
 */
import { useEffect, useRef, useState } from 'react';
import { useChat, useTranscriptions } from '@livekit/components-react';

type Sender = 'agent' | 'student';

interface MessageItem {
  /** Stable id from LiveKit (transcription streamInfo.id / chat message id). */
  id: string;
  text: string;
  sender: Sender;
  time: number;
  source: 'transcription' | 'chat';
}

const senderOf = (identity: string | undefined): Sender =>
  identity?.includes('student') ? 'student' : 'agent';

const AiTutorTranscript = () => {
  const transcriptions = useTranscriptions();
  const { chatMessages } = useChat();

  // First-seen timestamp per transcription, keyed by the segment's STABLE stream
  // id (not array index — useTranscriptions may reorder/prune as segments go
  // interim → final, which would shuffle index-keyed timestamps).
  const transcriptionTimesRef = useRef<Map<string, number>>(new Map());
  const [allMessages, setAllMessages] = useState<MessageItem[]>([]);

  useEffect(() => {
    transcriptions.forEach((t) => {
      const id = t.streamInfo.id;
      if (!transcriptionTimesRef.current.has(id)) {
        transcriptionTimesRef.current.set(id, Date.now());
      }
    });

    const transcriptItems: MessageItem[] = transcriptions.map((t) => ({
      id: t.streamInfo.id,
      text: t.text,
      sender: senderOf(t.participantInfo?.identity),
      time: transcriptionTimesRef.current.get(t.streamInfo.id) ?? Date.now(),
      source: 'transcription',
    }));

    const chatItems: MessageItem[] = chatMessages.map((m) => ({
      id: m.id,
      text: m.message,
      sender: senderOf(m.from?.identity),
      time: m.timestamp,
      source: 'chat',
    }));

    // Merge: walk both time-sorted lists in order.
    const merged: MessageItem[] = [];
    let ti = 0;
    let ci = 0;
    while (ti < transcriptItems.length || ci < chatItems.length) {
      if (ti >= transcriptItems.length) {
        merged.push(chatItems[ci++]);
      } else if (ci >= chatItems.length) {
        merged.push(transcriptItems[ti++]);
      } else if (transcriptItems[ti].time <= chatItems[ci].time) {
        merged.push(transcriptItems[ti++]);
      } else {
        merged.push(chatItems[ci++]);
      }
    }
    setAllMessages(merged);
  }, [transcriptions, chatMessages]);

  // "AI is thinking" — the latest message is from the student and the agent has
  // not responded yet. A recorder analog of the frontend's post-mic-release
  // indicator (we have no local mic to key off).
  const isThinking =
    allMessages.length > 0 && allMessages[allMessages.length - 1].sender === 'student';

  // Auto-follow the bottom. There is no user to scroll, so followBottom stays
  // true and the newest message is always in view — but we keep the frontend's
  // logic so manual review (if the page is ever opened by a human) still works.
  const containerRef = useRef<HTMLDivElement>(null);
  const followBottomRef = useRef(true);
  const [bottomSpacer, setBottomSpacer] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      followBottomRef.current = distanceFromBottom < 40;
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    const updateSpacer = () => setBottomSpacer(Math.round(el.clientHeight * 0.4));
    updateSpacer();
    const ro = new ResizeObserver(updateSpacer);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handle = requestAnimationFrame(() => {
      if (followBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(handle);
  }, [allMessages, isThinking, bottomSpacer]);

  return (
    <div className="flex flex-col border-l w-80 shrink-0 bg-neutral-900 border-white/10">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="text-sm font-medium text-white/80 font-poppins">Transcript</span>
      </div>
      <div ref={containerRef} className="flex-1 p-3 space-y-3 overflow-y-auto">
        {allMessages.length === 0 && (
          <p className="text-xs text-white/30 font-poppins">Waiting for AI Tutor to start...</p>
        )}
        {allMessages.map((m) => (
          <div
            key={`${m.source}-${m.id}`}
            className={`flex flex-col ${m.sender === 'agent' ? 'items-start' : 'items-end'}`}
          >
            <span className="text-[10px] text-white/30 font-poppins mb-0.5">
              {m.sender === 'agent' ? 'AI Tutor' : 'You'}
            </span>
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 text-xs font-poppins leading-relaxed ${
                m.sender === 'agent'
                  ? 'bg-indigo-500/20 text-indigo-100'
                  : 'bg-emerald-500/20 text-emerald-100'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {isThinking && (
          <div className="flex flex-col items-start">
            <span className="text-[10px] text-white/30 font-poppins mb-0.5">AI Tutor</span>
            <div
              className="bg-indigo-500/20 text-indigo-100 rounded-xl px-3 py-2 inline-flex items-center gap-1"
              aria-label="AI Tutor is thinking"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div style={{ height: bottomSpacer }} className="shrink-0" aria-hidden="true" />
      </div>
    </div>
  );
};

export default AiTutorTranscript;
